import {
    HttpClient,
    HttpErrorResponse
} from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Store } from "@ngrx/store";
import {
    Observable,
    of,
    throwError
} from "rxjs";
import {
    catchError,
    map,
    tap,
} from "rxjs/operators";
import { ToastAlertService } from "src/app/shared/utils/toast.service";
import {
    Auth,
    InvestorTypes,
    LoginCredentials,
    SignupCredentials,
    SingUpSendOtpResponse,
    SingUpVerifyOtpResponse,
    VerifyOtpCredentials
} from "../domain";
import { ApiEndpointService } from "./api-endpoint.service";
import { RequestKeys, ResponseData } from "src/app/core/contracts/api-contract";
import { GlobalService } from "./global.service";
import * as AuthActions from "src/app/core/state/auth/auth.action";
import { USER_TYPES, USER_TYPE_INVESTOR } from "src/app/shared/constants/startup-invetor-constants";
import md5 from 'md5';
import { httpFaultMessage } from 'src/app/shared/utils/http-fault.util';

@Injectable({
    providedIn: "root"
})
export class AuthService {
    constructor(
        private http: HttpClient,
        private toastService: ToastAlertService,
        private globalService: GlobalService,
        private store: Store
    ) { }

    /**
     * Attempt authentication.
     */
    public login(loginCredentials: LoginCredentials): Observable<any> {
        const url = ApiEndpointService.getEndpoint(ApiEndpointService.ENDPOINT.MOBILE_LOGIN);
        /* SAN-1911: typed against the backend's LoginDto, so a renamed or removed field is a compile error. */
        const params: RequestKeys<'LoginDto'> = {
            countryCode: loginCredentials.countryCode,
            email: loginCredentials.email
          };
        if(loginCredentials.mobileNumber) {
          params.mobileNumber = +loginCredentials.mobileNumber ? +loginCredentials.mobileNumber : '';
        }
        if(loginCredentials.email){
          params.email = loginCredentials.email;
        }
        // console.info(`login( Logging into API "${url}" with creds: ${params.countryCode} / ${params.mobileNumber} )`);
        return this.http.post(url, params).pipe(
            // map((res: any) => res.data),
            catchError((fault: HttpErrorResponse) => {
                console.warn(`loginFault( ${fault?.error?.message || fault?.message || fault?.status} )`);
                return throwError(() => fault);
            })
        );
    }


    /**
     * Attempt authentication.
     */
    public verifyOtp(loginCredentials: VerifyOtpCredentials): Observable<Auth> {
        const url = ApiEndpointService.getEndpoint(ApiEndpointService.ENDPOINT.MOBILE_LOGIN_VERIFY_OTP);
        /* SAN-1911: typed against the backend's VerifyUserDto (see contracts/api-contract.ts). */
        const params: RequestKeys<'VerifyUserDto'> = {
            countryCode: loginCredentials.countryCode,
            // mobileNumber:loginCredentials.mobileNumber?  loginCredentials.mobileNumber: '',
            code: md5(loginCredentials.code),
            // email: loginCredentials.email
        };
        if(loginCredentials.mobileNumber) {
          params.mobileNumber = +loginCredentials.mobileNumber ? +loginCredentials.mobileNumber : '';
        }
        if(loginCredentials.email){
          params.email = loginCredentials.email;
        }
        return this.http.post(url, params).pipe(
            /* SAN-1915: response typed from the backend's documented envelope, so a rename of data.user.* fails the build. */
            /* Only the envelope keys read below (data.user, data.accessToken) are checked: the DTO's user fields are string
               unions and omit countryCode, so they do not line up with the app's Auth enums, hence the cast on user. */
            map((response: { data: ResponseData<'AuthController_verifyUser'> }): Auth => {
                // console.info(`loginSuccess( Received access token: ${response.accessToken} )`);
                return {
                    ...(response.data.user as unknown as Omit<Auth, 'accessToken'>),
                    accessToken: response.data.accessToken,
                    // userId:response.data.user.userId,
                    // name:response.data.user.name,
                    // userGroup:response.data.user.userGroup,
                };
            }),
            catchError((fault: HttpErrorResponse) => {
                console.warn(`loginFault( ${fault?.error?.message || fault?.message || fault?.status} )`);
                this.toastService.showToast(this.globalService.getErrorMessage(fault, 'Error verifying the OTP!'), 'error');
                return throwError(() => fault);
            })
        );
    }

    public logout = (ignoreRedirect?) => {
        this.store.dispatch(new AuthActions.LogOut({ignoreRedirect}));
    }

    /**
     * SAN-384 hub->spoke redirect (auth.effect.ts login$) lands the browser
     * on a different origin than the one that just logged in, so
     * localStorage (never shared across subdomains) is empty there even
     * though the httpOnly accessToken cookie -- scoped to the shared
     * apiUrl host, not this frontend origin -- may still be valid.
     * AuthGuard calls this as a fallback to confirm the cookie session
     * before deciding to log out. Deliberately has no toast/logout side
     * effects of its own: the 401 interceptor and AuthGuard's own catch
     * already own that behavior for a genuinely expired session, and
     * duplicating it here would double up the toast/redirect.
     */
    public validateSession(): Observable<Auth> {
        const url = ApiEndpointService.getEndpoint(ApiEndpointService.ENDPOINT.USER_PROFILE);
        return this.http.get(url).pipe(
            map((response: any): Auth => ({
                userId: response.data.id,
                name: response.data.name,
                accountType: response.data.accountType,
            } as Auth))
        );
    }

    public verifyMobile(mobileNumber: string, userType :USER_TYPES= USER_TYPES.INVESTOR, investorType: InvestorTypes = InvestorTypes.INDIVIDUAL): Observable<any> {
        const url = ApiEndpointService.getEndpoint(ApiEndpointService.ENDPOINT.VERIFY_MOBILE + mobileNumber);
        return this.http.get(url,  {
          params : {
            userType,
            investorType
          }
        }).pipe(
            map((response: any): any => {
                return response;
            }),
            catchError((fault: HttpErrorResponse) => {
                this.toastService.showToast(fault?.error?.message || 'Error while verifying mobile number', 'error');
                return throwError(() => fault);
            })
        );
    }

    public verifyEmail(email: string, userType :USER_TYPES= USER_TYPES.INVESTOR, investorType: InvestorTypes = InvestorTypes.INDIVIDUAL): Observable<any> {
        const url = ApiEndpointService.getEndpoint(ApiEndpointService.ENDPOINT.VERIFY_EMAIL + email.toLowerCase());
        return this.http.get(url ,  {
          params: {
            userType,
            investorType
          }
        }).pipe(
            map((response: any): any => {
                return response;
            }),
            catchError((fault: HttpErrorResponse) => {
                this.toastService.showToast(fault?.error?.message || 'Error while verifying email address', 'error');
                return throwError(() => fault);
            })
        );
    }


    /**
     * SAN-384/386 (Decision #9) — live availability pre-check for a Partner's
     * subdomain abbreviation. Same `{isExist, message}` contract as
     * verifyEmail()/verifyMobile() above.
     *
     * Two deliberate differences from those two: no toast, and the error is
     * swallowed into `null` rather than re-thrown. The backend route ships with
     * SAN-387, so until then every call 404s — the abbreviation field must stay
     * usable (just without live feedback) instead of toasting on every keystroke
     * or blocking registration.
     */
    public verifyPartnerAbbreviation(abbreviation: string): Observable<any> {
        const url = ApiEndpointService.getEndpoint(ApiEndpointService.ENDPOINT.VERIFY_PARTNER_ABBREVIATION + abbreviation);
        return this.http.get(url).pipe(
            map((response: any): any => {
                return response;
            }),
            catchError(() => {
                return of(null);
            })
        );
    }

    public markEmailAsVerified(emailAddress, verificationId) {
      const url = ApiEndpointService.getEndpoint(ApiEndpointService.ENDPOINT.VERIFY_EMAIL );
      return this.http.post(url ,  {
        emailAddress, verificationId
      }).pipe(
          map((response: any): any => {
              return response;
          }),
          catchError((fault: HttpErrorResponse) => {
              this.toastService.showToast(fault?.error?.message || 'Error while verifying email address', 'error');
              return throwError(() => fault);
          })
      );
    }

    public deleteAccount() {
      const url = ApiEndpointService.getEndpoint(ApiEndpointService.ENDPOINT.DELETE_ACCOUNT );
      return this.http.delete(url).pipe(
          // tap(()=>{
          //   this.logout()
          // }),
          map((response: any): any => {
              return response;
          }),
          catchError((fault: HttpErrorResponse) => {
              this.toastService.showToast(fault?.error?.message || 'Error while deleting account', 'error');
              return throwError(() => fault);
          })
      );
    }

    public deactivateAccount(currentProfileActivateStatus) {
      const url = ApiEndpointService.getEndpoint(ApiEndpointService.ENDPOINT.DEACTIVATE_ACCOUNT );
      return this.http.patch(url, {}).pipe(
          map((response: any): any => {
            this.toastService.showToast(currentProfileActivateStatus ? 'Account Deactivated Successfully.' : 'Account Re-activated Successfully.');
            return response;
          }),
          catchError((fault: HttpErrorResponse) => {
              this.toastService.showToast(fault?.error?.message || 'Error while deactivate account', 'error');
              return throwError(() => fault);
          })
      );
    }




    public sendWhatsappOTP(signupCredentials: SignupCredentials): Observable<any> {
      const url = ApiEndpointService.getEndpoint(ApiEndpointService.ENDPOINT.WHATSAPP_SEND_OTP);
      const params = {
        type: 'whatsapp',
        countryCode: signupCredentials.countryCode,
        mobileNumber: signupCredentials.mobileNumber,
        emailAddress: signupCredentials.emailAddress,
        name: signupCredentials.name
      };
      return this.http.post(url, params).pipe(
        map((response: SingUpSendOtpResponse) => {

          console.trace('============== sendOTP() response =============');

          return response.data
        }),
        catchError((fault: HttpErrorResponse) => {
          // SC-SAAS-FRONTEND-6S / SAN-470 -- an empty-body failure (504, network error) has no error.message, so this logged "sendOTPFault( undefined )"; include the status instead.
          console.warn(`sendOTPFault( ${httpFaultMessage(fault, `HTTP ${fault?.status}`)} )`);
          return throwError(() => fault);
        })
      );
    }

    public verifyWhatsappOTP(signupCredentials: SignupCredentials): Observable<any> {
      const url = ApiEndpointService.getEndpoint(ApiEndpointService.ENDPOINT.WHATSAPP_VERIFY_OTP);
      const params = {
        type: 'whatsapp',
        countryCode: signupCredentials.countryCode,
        mobileNumber: signupCredentials.mobileNumber,
        emailAddress: signupCredentials.emailAddress,
        code: md5(signupCredentials.code.toString()),
        name: signupCredentials.name
      };
      return this.http.post(url, params).pipe(
        map((response: SingUpVerifyOtpResponse) => {
          return response.data
        }),
        catchError((fault: HttpErrorResponse) => {
          // SAN-1662 -- an empty-body failure (504, network error) has no error.message, so this logged "verifyOTPFault( undefined )"; include the status instead (same fix as SAN-673 in sign-up.service).
          console.warn(`verifyOTPFault( ${httpFaultMessage(fault, `HTTP ${fault?.status}`)} )`);
          this.toastService.showToast(fault?.error?.message || 'Error while verifying OTP', 'error');
          return throwError(() => fault);
        })
      );
    }

    public logoutFromApi() {
      const url = ApiEndpointService.getEndpoint(ApiEndpointService.ENDPOINT.USER ) + 'logout';
      return this.http.get(url, {}).pipe(
          map((response: any): any => {
            // this.toastService.showToast(currentProfileActivateStatus ? 'Account Deactivated Successfully.' : 'Account Re-activated Successfully.');
            return response;
          }),
          catchError((fault: HttpErrorResponse) => {
              // this.toastService.showToast(fault?.error?.message || 'Error while deactivate account', 'error');
              return throwError(() => fault);
          })
      );
    }


    public importProfileOtpRequest(payload): Observable<any> {
      const url = ApiEndpointService.getEndpoint(ApiEndpointService.ENDPOINT.AUTH_EXTERNAL);
      return this.http.post(url + '/login', payload).pipe(
          catchError((fault: HttpErrorResponse) => {
              console.warn(`loginFault( ${fault.error?.message || fault.message || fault.status} )`);
              return throwError(() => fault);
          })
      );
  }

  public verifyImportProfileOtpRequest(payload): Observable<any> {
    const url = ApiEndpointService.getEndpoint(ApiEndpointService.ENDPOINT.AUTH_EXTERNAL);
    payload['code'] =  md5(payload.code.toString());
    return this.http.post(url+'/login/verify/import', payload).pipe(
      map((res: any) => {
        this.toastService.showToast(res.message|| 'Profile imported successfully');
        return res.data
      }),
        catchError((fault: HttpErrorResponse) => {
            console.warn(`loginFault( ${fault.error?.message || fault.message || fault.status} )`);
            return throwError(() => fault);
        })
    );
}
}
